import { useEffect, useRef, useState } from 'react';
import { IconPhone, IconPhoneOff, IconMic, IconMicOff, IconAlert, IconGlobe } from '../icons.jsx';
import { languageName } from '../lang.js';

/** Wall-clock time for one transcript line. Invalid dates render as empty. */
function clockTime(at) {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * The in-call surface.
 *
 * The call engine is created here and torn down with the component, so there is
 * exactly one per call and closing the panel cannot leave a microphone live.
 * `@desk/client/voicecall` is imported dynamically, so a visitor who only ever
 * types never downloads it — the reference system ships one 878 KB bundle to
 * everybody, and this is the structural answer to that.
 *
 * The visitor's microphone is never sent to the agent. It feeds recognition in
 * this browser and nothing else; what the agent hears is synthesised from the
 * translated text on their own side. So there is no point at which the agent's
 * original voice could reach this visitor, and none at which theirs could reach
 * the agent.
 */
export function CallPanel({
  grant,
  status,
  queuePosition,
  subtitles,
  translation,
  translationMessage,
  socket,
  speechLang,
  listenLang,
  languageNames,
  onHangUp,
}) {
  const [muted, setMuted] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [connState, setConnState] = useState('new');
  const [interim, setInterim] = useState('');
  const [captions, setCaptions] = useState({ recognise: false, synthesise: false });
  const [fallback, setFallback] = useState(null);
  const callRef = useRef(null);
  const subtitleEnd = useRef(null);
  /** Clauses this endpoint has sent, so the round trip can be attributed. */
  const sent = useRef([]);
  /** Loaded with the call chunk, so the trace helper is not in the eager bundle. */
  const debugRef = useRef(null);

  /* ------------------------------------------------- the peer connection */

  useEffect(() => {
    if (!grant || !socket) return;
    let cancelled = false;
    const offs = [];

    (async () => {
      try {
        const mod = await import('@desk/client/voicecall');
        const { VoiceCall, callSupport } = mod;
        debugRef.current = mod.logTiming;

        const support = callSupport();
        if (!support.supported) {
          setMediaError(support.reason ?? 'Calling is not available in this browser.');
          return;
        }
        if (!cancelled) {
          setCaptions({ recognise: support.canRecognise, synthesise: support.canSynthesise });
        }

        if (grant.mode !== 'p2p') {
          setMediaError('This build expects browser-side translated calling. Check MEDIA_MODE.');
          return;
        }

        const call = new VoiceCall({
          grant,
          speechLang,
          listenLang,
          handlers: {
            onConnectionState: (s) => !cancelled && setConnState(s),
            onInterim: (t) => !cancelled && setInterim(t),
            onSpeaking: (speaking) => !cancelled && !speaking && setInterim(''),
            onSynthesisProblem: (m) => !cancelled && setFallback(m),
            onTiming: (t) => debugRef.current?.(t),
            onError: (m) => !cancelled && setMediaError(m),
          },
        });

        // No signalling: there is no media to negotiate. Everything the far end
        // hears travels as text over the socket this widget already holds.
        await call.start();
        if (cancelled) {
          call.close();
          return;
        }
        callRef.current = call;

        // Recognition of our own speech; the finished clause goes up as text.
        // `sentAt` is kept so the round trip can be attributed when the
        // translated segment comes back.
        call.startRecognition((text, sttMs) => {
          setInterim('');
          sent.current.push({ text, sttMs, at: Date.now() });
          if (sent.current.length > 20) sent.current.shift();
          socket.raw.emit('xlate:final', { callId: grant.callId, text });
        });
      } catch (err) {
        if (!cancelled) {
          setMediaError(err instanceof Error ? err.message : 'Could not start the call');
        }
      }
    })();

    return () => {
      cancelled = true;
      for (const off of offs) off();
      callRef.current?.close();
      callRef.current = null;
    };
  }, [grant, socket, speechLang, listenLang]);

  /* --------------------------------------------- speak incoming subtitles */

  const spoken = useRef(new Set());
  useEffect(() => {
    const call = callRef.current;
    if (!call) return;
    for (const line of subtitles) {
      // Only the far end's words are spoken, and each one exactly once.
      if (line.own || spoken.current.has(line.segId)) continue;
      spoken.current.add(line.segId);
      // An untranslated segment is the agent's own language passed through —
      // either every provider failed, or the one that answered does not really
      // translate. It belongs on screen, not in the ear.
      if (!line.translated) {
        debugRef.current?.({
          segId: line.segId,
          mtMs: line.mtMs,
          provider: line.provider,
          translated: false,
          spoken: false,
        });
        continue;
      }
      call.speak(line.text, {
        segId: line.segId,
        timing: {
          segId: line.segId,
          mtMs: line.mtMs,
          provider: line.provider,
          translated: true,
          spoken: false,
          e2eStartedAt: line.arrivedAt,
        },
      });
    }
  }, [subtitles]);


  /*
   * Speaker-side timing for our own clauses.
   *
   * Measured here rather than on the listener because both ends of this
   * measurement are on ONE clock: release to echo-back is network plus
   * translation with no skew to correct for. Subtracting the server's own
   * `mtMs` leaves the socket round trip, which is the number that actually
   * says whether a slow call is the vendor or the link.
   */
  const timedOwn = useRef(new Set());
  useEffect(() => {
    for (const line of subtitles) {
      if (!line.own || timedOwn.current.has(line.segId)) continue;
      timedOwn.current.add(line.segId);
      const idx = sent.current.findIndex((s) => s.text === line.original || s.text === line.text);
      const match = idx >= 0 ? sent.current.splice(idx, 1)[0] : null;
      if (!match) continue;
      const rtt = Date.now() - match.at;
      debugRef.current?.({
        segId: line.segId,
        sttMs: match.sttMs,
        mtMs: line.mtMs,
        netMs: line.mtMs !== undefined ? Math.max(0, rtt - line.mtMs) : undefined,
        provider: line.provider,
        translated: line.translated,
        spoken: false,
      });
    }
  }, [subtitles]);

  useEffect(() => {
    subtitleEnd.current?.scrollIntoView({ block: 'end' });
  }, [subtitles.length, interim]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    callRef.current?.setMuted(next);
  };

  const live = connState === 'connected';

  // Minted per endpoint by the server, so the widget learns the agent's
  // language without having to be told separately.
  const xlate = grant?.translation;

  const heading =
    status === 'queued'
      ? 'Waiting for an agent'
      : status === 'ringing'
        ? 'Connecting you'
        : status === 'connecting'
          ? 'Connecting'
          : live
            ? 'On a call'
            : 'Connecting audio';

  return (
    <div className="call">
      <div className="call-head">
        <span className={`call-orb ${live ? 'live' : 'waiting'}`} aria-hidden>
          <IconPhone size={19} />
        </span>
        <div className="call-head-text">
          <strong>{heading}</strong>
          <span className="call-sub">
            {!live
              ? 'Please stay on this screen'
              : xlate?.active && translation === 'ok'
                ? 'Speak normally — we translate as you go'
                : 'Speak normally'}
          </span>
        </div>
        {queuePosition !== null && queuePosition > 1 && (
          <span className="call-queue">Position {queuePosition}</span>
        )}
      </div>

      {xlate?.active && translation === 'ok' && (
        <p className="xlate-chip" role="status">
          <IconGlobe size={13} />
          <span className="xlate-chip-label">Live translation</span>
          <span className="xlate-chip-pair">
            {languageName(xlate.peerLang, languageNames)}
            <i aria-hidden> ↔ </i>
            {languageName(xlate.selfLang, languageNames)}
          </span>
        </p>
      )}

      {translation !== 'ok' && (
        <p className="notice warn" role="status">
          <IconAlert size={14} />
          {translationMessage || 'Translation unavailable — continuing untranslated'}
        </p>
      )}

      {fallback && (
        <p className="notice warn" role="status">
          <IconAlert size={14} />
          {fallback}
        </p>
      )}

      {/*
        A browser missing either half is refused by `callSupport()` before the
        call starts, so there is no mid-call notice to show here: with no
        original audio path there would be nothing to fall back to. What remains
        is the case where synthesis exists but a particular utterance failed,
        which is the `fallback` notice above.
      */}

      {mediaError && (
        <p className="notice danger" role="alert">
          <IconAlert size={14} />
          {mediaError}
        </p>
      )}

      <div className="subtitles" aria-live="polite">
        {subtitles.length === 0 && !interim ? (
          <p className="subtitle-hint">Subtitles appear here as you speak.</p>
        ) : (
          subtitles.map((s) => (
            <div key={s.segId} className={s.own ? 'subtitle own' : 'subtitle'}>
              <p className="subtitle-who">
                <span>
                  {s.own ? 'You' : 'Agent'}
                  {s.typed && <span className="subtitle-typed"> · typed</span>}
                </span>
                <time>{clockTime(s.at)}</time>
              </p>
              <p className="subtitle-text">{s.text}</p>
              {!s.own && !s.translated && (
                <p className="subtitle-untranslated">
                  Not translated — shown in the agent&rsquo;s language, and not read aloud.
                </p>
              )}
              {/*
                The other rendition of the same segment, never a second message.
                For an agent line this is the English they actually spoke, kept
                beside the translation the way chat keeps THEIR WORDS.
              */}
              {s.original && s.original !== s.text && (
                <p className="subtitle-original">
                  <span className="subtitle-original-label">
                    {s.own ? (s.typed ? 'AGENT READS' : 'AGENT HEARS') : 'THEIR WORDS'}
                  </span>
                  {s.original}
                </p>
              )}
            </div>
          ))
        )}
        {interim && <p className="subtitle own interim">{interim}</p>}
        <div ref={subtitleEnd} />
      </div>

      <div className="call-actions">
        <button type="button" className="ghost" onClick={toggleMute} disabled={!live}>
          {muted ? <IconMicOff size={15} /> : <IconMic size={15} />}
          {muted ? 'Unmute' : 'Mute'}
        </button>
        <button type="button" className="danger" onClick={onHangUp}>
          <IconPhoneOff size={15} />
          {status === 'active' ? 'End call' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}
